import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  CAMPAIGN_TYPES,
  Campaign,
  CampaignEventSource,
  CampaignEventType,
  CampaignMediaType,
  HighlightedCampaignEligibility
} from '../../../../interface/campaign';
import { HighlightedCampaignPopupService } from '../../../../service/highlighted-campaign-popup.service';

export interface HighlightedCampaignDialogData {
  eligibility: HighlightedCampaignEligibility;
  eventSource: CampaignEventSource;
}

type EmbeddedVideoProvider = 'youtube' | 'vimeo';

interface EmbeddedVideoConfiguration {
  provider: EmbeddedVideoProvider;
  embedUrl: string;
}

interface YoutubePlayerStateChangeEvent {
  data: number;
}

interface YoutubePlayer {
  destroy(): void;
  getCurrentTime(): number;
  getDuration(): number;
}

interface VimeoTimeUpdateEvent {
  percent: number;
}

interface VimeoPlayer {
  destroy(): Promise<void>;
  on(eventName: 'play', callback: () => void): void;
  on(eventName: 'timeupdate', callback: (data: VimeoTimeUpdateEvent) => void): void;
  on(eventName: 'ended', callback: () => void): void;
}

interface YoutubePlayerInstanceConfig {
  events: {
    onStateChange: (event: YoutubePlayerStateChangeEvent) => void;
  };
}

interface YoutubeApi {
  Player: new (element: HTMLIFrameElement, config: YoutubePlayerInstanceConfig) => YoutubePlayer;
}

interface VimeoApi {
  Player: new (element: HTMLIFrameElement) => VimeoPlayer;
}

declare global {
  interface Window {
    YT?: YoutubeApi;
    Vimeo?: VimeoApi;
    onYouTubeIframeAPIReady?: () => void;
  }
}

@Component({
  selector: 'app-highlighted-campaign-dialog',
  templateUrl: './highlighted-campaign-dialog.component.html',
  styleUrls: ['./highlighted-campaign-dialog.component.scss']
})
export class HighlightedCampaignDialogComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('campaignVideoFrame') campaignVideoFrame?: ElementRef<HTMLIFrameElement>;

  private static youtubeApiReady: Promise<YoutubeApi> | null = null;
  private static vimeoApiReady: Promise<VimeoApi> | null = null;

  private static readonly YOUTUBE_PLAYER_STATE = {
    ENDED: 0,
    PLAYING: 1,
    PAUSED: 2,
  };

  readonly mediaTypes = CampaignMediaType;
  readonly campaign: Campaign;
  readonly whatsappLink: string | null;

  videoEmbedUrl: SafeResourceUrl | null = null;
  videoPosterUrl: string | null = null;

  private closeTracked = false;
  private videoStarted = false;
  private videoCompleted = false;
  private readonly trackedVideoMilestones = new Set<number>();
  private embeddedVideoProvider: EmbeddedVideoProvider | null = null;
  private youtubePlayer: YoutubePlayer | null = null;
  private vimeoPlayer: VimeoPlayer | null = null;
  private youtubeProgressIntervalId: number | null = null;

  constructor(
    public dialogRef: MatDialogRef<HighlightedCampaignDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: HighlightedCampaignDialogData,
    private sanitizer: DomSanitizer,
    private highlightedCampaignPopupService: HighlightedCampaignPopupService
  ) {
    this.campaign = data.eligibility.campaign;
    this.whatsappLink = this.highlightedCampaignPopupService.buildWhatsAppLink(this.campaign);
    this.videoPosterUrl = null;
  }

  async ngOnInit(): Promise<void> {
    this.prepareVideoEmbed();

    this.dialogRef.beforeClosed().subscribe(() => {
      void this.trackClose();
    });

    await this.highlightedCampaignPopupService.registerEvent(
      this.data.eligibility,
      CampaignEventType.POPUP_VIEWED,
      this.data.eventSource
    );
  }

  async ngAfterViewInit(): Promise<void> {
    await this.initializeEmbeddedVideoTracking();
  }

  ngOnDestroy(): void {
    this.stopYoutubeProgressTracking();

    if (this.youtubePlayer) {
      this.youtubePlayer.destroy();
      this.youtubePlayer = null;
    }

    if (this.vimeoPlayer) {
      void this.vimeoPlayer.destroy();
      this.vimeoPlayer = null;
    }

    void this.trackClose();
  }

  get canDismiss(): boolean {
    return this.campaign?.displaySettings?.canBeDismissed !== false;
  }

  get isVideoCampaign(): boolean {
    return this.campaign?.mediaAsset?.mediaType === CampaignMediaType.VIDEO;
  }

  get isImageCampaign(): boolean {
    return this.campaign?.mediaAsset?.mediaType === CampaignMediaType.IMAGE;
  }

  get shouldRenderEmbeddedVideo(): boolean {
    return this.isVideoCampaign && Boolean(this.videoEmbedUrl);
  }

  get campaignTypeLabel(): string {
    if (this.campaign?.campaignType === CAMPAIGN_TYPES.ALERTA) {
      return 'Alerta';
    }

    if (this.campaign?.campaignType === CAMPAIGN_TYPES.NOVIDADES) {
      return 'Novidades';
    }

    return 'Informativo';
  }

  close(): void {
    this.dialogRef.close();
  }

  async openWhatsApp(): Promise<void> {
    if (!this.whatsappLink) {
      return;
    }

    await this.highlightedCampaignPopupService.registerEvent(
      this.data.eligibility,
      CampaignEventType.WHATSAPP_CLICKED,
      this.data.eventSource,
      {
        additionalData: {
          destination: this.whatsappLink
        }
      }
    );

    window.open(this.whatsappLink, '_blank', 'noopener,noreferrer');
  }

  async onHtml5VideoPlay(): Promise<void> {
    await this.trackVideoStarted();
  }

  async onHtml5VideoTimeUpdate(event: Event): Promise<void> {
    const target = event.target as HTMLVideoElement | null;
    if (!target || !target.duration) {
      return;
    }

    const progressPercentage = Math.floor((target.currentTime / target.duration) * 100);
    await this.trackVideoProgress(progressPercentage);
  }

  async onHtml5VideoEnded(): Promise<void> {
    await this.trackVideoCompleted();
  }

  private prepareVideoEmbed(): void {
    if (!this.isVideoCampaign) {
      return;
    }

    const mediaUrl = this.campaign?.mediaAsset?.mediaUrl ?? '';
    const embeddedVideoConfiguration = this.toEmbeddableVideoUrl(mediaUrl);

    if (embeddedVideoConfiguration) {
      this.embeddedVideoProvider = embeddedVideoConfiguration.provider;
      this.videoEmbedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(embeddedVideoConfiguration.embedUrl);
    }
  }

  private async initializeEmbeddedVideoTracking(): Promise<void> {
    const iframeElement = this.campaignVideoFrame?.nativeElement;

    if (!iframeElement || !this.embeddedVideoProvider) {
      return;
    }

    if (this.embeddedVideoProvider === 'youtube') {
      await this.initializeYoutubeTracking(iframeElement);
      return;
    }

    await this.initializeVimeoTracking(iframeElement);
  }

  private async initializeYoutubeTracking(iframeElement: HTMLIFrameElement): Promise<void> {
    const youtubeApi = await HighlightedCampaignDialogComponent.loadYoutubeApi();

    this.youtubePlayer = new youtubeApi.Player(iframeElement, {
      events: {
        onStateChange: event => {
          void this.handleYoutubeStateChange(event);
        }
      }
    });
  }

  private async initializeVimeoTracking(iframeElement: HTMLIFrameElement): Promise<void> {
    const vimeoApi = await HighlightedCampaignDialogComponent.loadVimeoApi();

    this.vimeoPlayer = new vimeoApi.Player(iframeElement);
    this.vimeoPlayer.on('play', () => {
      void this.trackVideoStarted();
    });
    this.vimeoPlayer.on('timeupdate', ({ percent }) => {
      void this.trackVideoProgress(Math.floor(percent * 100));
    });
    this.vimeoPlayer.on('ended', () => {
      void this.trackVideoCompleted();
    });
  }

  private async handleYoutubeStateChange(event: YoutubePlayerStateChangeEvent): Promise<void> {
    switch (event.data) {
      case HighlightedCampaignDialogComponent.YOUTUBE_PLAYER_STATE.PLAYING:
        await this.trackVideoStarted();
        this.startYoutubeProgressTracking();
        return;
      case HighlightedCampaignDialogComponent.YOUTUBE_PLAYER_STATE.PAUSED:
        this.stopYoutubeProgressTracking();
        return;
      case HighlightedCampaignDialogComponent.YOUTUBE_PLAYER_STATE.ENDED:
        this.stopYoutubeProgressTracking();
        await this.trackVideoProgress(100);
        await this.trackVideoCompleted();
        return;
      default:
        return;
    }
  }

  private startYoutubeProgressTracking(): void {
    if (this.youtubeProgressIntervalId || !this.youtubePlayer) {
      return;
    }

    this.youtubeProgressIntervalId = window.setInterval(() => {
      void this.syncYoutubeProgress();
    }, 1000);
  }

  private stopYoutubeProgressTracking(): void {
    if (!this.youtubeProgressIntervalId) {
      return;
    }

    window.clearInterval(this.youtubeProgressIntervalId);
    this.youtubeProgressIntervalId = null;
  }

  private async syncYoutubeProgress(): Promise<void> {
    if (!this.youtubePlayer) {
      return;
    }

    const duration = this.youtubePlayer.getDuration();
    if (!duration) {
      return;
    }

    const currentTime = this.youtubePlayer.getCurrentTime();
    const progressPercentage = Math.floor((currentTime / duration) * 100);
    await this.trackVideoProgress(progressPercentage);
  }

  private async trackVideoStarted(): Promise<void> {
    if (this.videoStarted) {
      return;
    }

    this.videoStarted = true;
    await this.highlightedCampaignPopupService.registerEvent(
      this.data.eligibility,
      CampaignEventType.VIDEO_STARTED,
      this.data.eventSource
    );
  }

  private async trackVideoProgress(progressPercentage: number): Promise<void> {
    const milestones = [25, 50, 75];
    const milestone = milestones.find(currentMilestone => progressPercentage >= currentMilestone && !this.trackedVideoMilestones.has(currentMilestone));

    if (!milestone) {
      return;
    }

    this.trackedVideoMilestones.add(milestone);
    await this.highlightedCampaignPopupService.registerEvent(
      this.data.eligibility,
      CampaignEventType.VIDEO_PROGRESS,
      this.data.eventSource,
      {
        additionalData: {
          progressPercentage: milestone
        }
      }
    );
  }

  private async trackVideoCompleted(): Promise<void> {
    if (this.videoCompleted) {
      return;
    }

    this.videoCompleted = true;
    await this.highlightedCampaignPopupService.registerEvent(
      this.data.eligibility,
      CampaignEventType.VIDEO_COMPLETED,
      this.data.eventSource
    );
  }

  private toEmbeddableVideoUrl(mediaUrl: string): EmbeddedVideoConfiguration | null {
    if (!mediaUrl?.trim()) {
      return null;
    }

    const normalizedUrl = mediaUrl.trim();
      const youtubeMatch = normalizedUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/)([^&?/]+)/i);
    if (youtubeMatch?.[1]) {
      return {
        provider: 'youtube',
        embedUrl: `https://www.youtube.com/embed/${youtubeMatch[1]}?rel=0&enablejsapi=1`
      };
    }

    const vimeoMatch = normalizedUrl.match(/vimeo\.com\/(\d+)/i);
    if (vimeoMatch?.[1]) {
      return {
        provider: 'vimeo',
        embedUrl: `https://player.vimeo.com/video/${vimeoMatch[1]}`
      };
    }

    return null;
  }

  private static loadYoutubeApi(): Promise<YoutubeApi> {
    if (window.YT?.Player) {
      return Promise.resolve(window.YT);
    }

    if (!HighlightedCampaignDialogComponent.youtubeApiReady) {
      HighlightedCampaignDialogComponent.youtubeApiReady = new Promise((resolve, reject) => {
        const existingScript = document.querySelector('script[data-campaign-youtube-api="true"]');

        window.onYouTubeIframeAPIReady = () => {
          if (window.YT?.Player) {
            resolve(window.YT);
          } else {
            reject(new Error('YouTube API indisponível após carregamento.'));
          }
        };

        if (existingScript) {
          return;
        }

        const script = document.createElement('script');
        script.src = 'https://www.youtube.com/iframe_api';
        script.async = true;
        script.dataset['campaignYoutubeApi'] = 'true';
        script.onerror = () => reject(new Error('Falha ao carregar API do YouTube.'));
        document.body.appendChild(script);
      });
    }

    return HighlightedCampaignDialogComponent.youtubeApiReady;
  }

  private static loadVimeoApi(): Promise<VimeoApi> {
    if (window.Vimeo?.Player) {
      return Promise.resolve(window.Vimeo);
    }

    if (!HighlightedCampaignDialogComponent.vimeoApiReady) {
      HighlightedCampaignDialogComponent.vimeoApiReady = new Promise((resolve, reject) => {
        const existingScript = document.querySelector('script[data-campaign-vimeo-api="true"]');
        if (existingScript) {
          existingScript.addEventListener('load', () => {
            if (window.Vimeo?.Player) {
              resolve(window.Vimeo);
            }
          }, { once: true });
          existingScript.addEventListener('error', () => reject(new Error('Falha ao carregar API do Vimeo.')), { once: true });
          return;
        }

        const script = document.createElement('script');
        script.src = 'https://player.vimeo.com/api/player.js';
        script.async = true;
        script.dataset['campaignVimeoApi'] = 'true';
        script.onload = () => {
          if (window.Vimeo?.Player) {
            resolve(window.Vimeo);
            return;
          }

          reject(new Error('Vimeo API indisponível após carregamento.'));
        };
        script.onerror = () => reject(new Error('Falha ao carregar API do Vimeo.'));
        document.body.appendChild(script);
      });
    }

    return HighlightedCampaignDialogComponent.vimeoApiReady;
  }

  private async trackClose(): Promise<void> {
    if (this.closeTracked) {
      return;
    }

    this.closeTracked = true;
    await this.highlightedCampaignPopupService.registerEvent(
      this.data.eligibility,
      CampaignEventType.POPUP_DISMISSED,
      this.data.eventSource
    );
  }
}